import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig } from '@angular/platform-browser';
import { EnumApiScales } from '@library/store/measures/measures.interface';
import { Animation, Chart, ChartConfiguration, InteractionItem, registerables } from 'chart.js';
import 'chartjs-adapter-moment';
import * as moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import { CustomBarController, CustomBarElement, CustomLineController, CustomLineElement, CustomPointElement } from './custom.controller';

Chart.register(
  CustomBarController,
  CustomBarElement,
  CustomLineController,
  CustomPointElement,
  CustomLineElement,
);

declare module 'chart.js' {
  interface Chart {
    _updateDatasets(mode: string): void;
  }

  interface ChartTypeRegistry {
    customBar: ChartTypeRegistry['bar'];
    customLine: ChartTypeRegistry['line'];
  }
}

// Todo: make it scoped to Chart class
let _animationStartDate: number;

class CustomChart extends Chart {
  _updateDatasets(mode: string) {
    _animationStartDate = Date.now();
    super._updateDatasets(mode);
  }
}

Object.defineProperty(Animation.prototype, "_start", {
  get: function () {
    return this.__start;
  },
  set: function (val) {
    if (!this.startDate) {
      this._startDate = _animationStartDate;
      this.__start = this._startDate;
    } else {
      this.__start = val;
    }
  }
});

Chart.register(...registerables);

@Component({
  selector: 'lib-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent {

  @Input() config: ChartConfiguration;

  @Input() xMin: number;
  @Input() xMax: number;

  @Input() bounds: {[key: string]: {min: number, max: number}};

  @Input() apiScale: EnumApiScales;

  // @deprecated to remove when settingslegrand updated
  @Input() enablePan = false;
  @Input() enableSwipe = false;

  @Output() public select = new EventEmitter<InteractionItem>();
  selectSubscription: Subscription;

  get apiScaleStep() {
    return ApiScalesSteps[this.apiScale];
  }

  @ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>;

  public chart: Chart;

  public hammerInstance;

  @Output() swipeLeft = new EventEmitter();
  @Output() swipeRight = new EventEmitter();

  constructor(
    @Inject(HAMMER_GESTURE_CONFIG) public hammerGestureConfig: HammerGestureConfig,
  ) { }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.config && simpleChanges.config.previousValue) {
      this.chart?.destroy();

      this.updateMinMax();

      const animation = this.config.options.animation;
      this.config.options.animation = false;

      this.chart = new CustomChart(this.canvas.nativeElement, this.config);

      this.config.options.animation = animation;

      this.setupListeners();
    } else if (this.chart) {
      this.updateMinMax();
      this.chart.update();
    }
  }

  ngAfterViewInit(): void {
    this.updateMinMax();
    this.chart = new CustomChart(this.canvas.nativeElement, this.config);
    this.setupListeners();

    this.hammerInstance = this.hammerGestureConfig.buildHammer(this.canvas.nativeElement);

    this.hammerInstance.on('swiperight', this.onSwipeRight);
    this.hammerInstance.on('swipeleft', this.onSwipeLeft);
  }

  updateMinMax() {
    if (this.bounds) {
      const bounds = Object.values(this.bounds);
      const maxTimeDiff = Math.max(...bounds.map(b => b.max - b.min));
      Object.entries(this.bounds).forEach(([scale, {min, max}]) => {
        this.config.options.scales[scale].min = min - moment.duration(this.apiScaleStep.amount, this.apiScaleStep.unit).asMilliseconds() / 2
        // This handle cases like when 2 different month with different number
        // of days are shown in the same graphs
        this.config.options.scales[scale].max = (min + maxTimeDiff) - moment.duration(this.apiScaleStep.amount, this.apiScaleStep.unit).asMilliseconds() / 2;
      });
    }
    else {
      this.config.options.scales.x.min = this.xMin - moment.duration(this.apiScaleStep.amount, this.apiScaleStep.unit).asMilliseconds() / 2
      this.config.options.scales.x.max = this.xMax - moment.duration(this.apiScaleStep.amount, this.apiScaleStep.unit).asMilliseconds() / 2;
    }
  }

  setupListeners() {
    if (this.selectSubscription) {
      this.selectSubscription.unsubscribe();
    }
    if (this.config.options.plugins['bar-selection']) {
      this.selectSubscription = this.config.options.plugins['bar-selection'].selectedIndex$.subscribe(this.select);
    }
  }

  onSwipeRight = (event) => {
    if (Math.abs(event.overallVelocityX) >= 0.8) {
      this.swipeRight.next(event);
    }
  }

  onSwipeLeft = (event) => {
    if (Math.abs(event.overallVelocityX) >= 0.8) {
      this.swipeLeft.next(event);
    }
  }

  ngOnDestroy() {
    if (this.selectSubscription) {
      this.selectSubscription.unsubscribe();
    }
  }
}

const ApiScalesSteps: Record<EnumApiScales, {amount: number, unit: moment.unitOfTime.DurationConstructor}>  = {
  [EnumApiScales.FIVE_MINUTES]: {
    amount: 5,
    unit: 'minutes'
  },
  [EnumApiScales.ONE_HOUR]: {
    amount: 1,
    unit: 'hour'
  },
  [EnumApiScales.THREE_HOURS]: {
    amount: 3,
    unit: 'hours'
  },
  [EnumApiScales.ONE_DAY]: {
    amount: 1,
    unit: 'day'
  },
  [EnumApiScales.ONE_WEEK]: {
    amount: 1,
    unit: 'week'
  },
  [EnumApiScales.ONE_MONTH]: {
    amount: 1,
    unit: 'month'
  },
};